import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import { COOKING_FREQUENCY, ORGANIC_OPTIONS } from 'constants/signup';
import { plansV1Options } from 'data/subscriptionData';
import EditPlansModule from 'features/account/my-plan/EditPlansModule/EditPlansModule';
import TaxesApply from 'features/account/my-plan/TaxesApply';
import { Alert } from 'honeydew/Alert';
import Card from 'honeydew/Card';
import { Divider } from 'honeydew/Divider';
import RecurringIcon from 'honeydew/icons/FoodPreferences/RecurringIcon';
import { useAccountPlusMembershipHook } from 'hooks/useAccountPlusMembershipHook';
import { trackEvent } from 'utils/analyticsUtils';

function PlanSummary({ account, autoShip }) {
    const { cartMinimum, dietaryPreferences, planID, planPreferences, planType, shippingFee } = account ?? {};
    const {
        inPlusMembershipTreatment,
        membership,
        plusMembershipBranding,
        shippingFees: plusMembershipShippingFees,
        trial,
    } = useAccountPlusMembershipHook();

    const { freeShippingEligible, freeShippingThreshold } = account.nextOrder.cartExperience || {};
    const { addOnPlans, basePlan, maxBoxRange, minBoxRange, plans } = planPreferences ?? {};
    const { cookingFrequency, dietaryPreference, glutenFree, householdSize, kosher, nonGMO, percentOrganic } =
        dietaryPreferences ?? {};

    const [error, setError] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [planRange, setPlanRange] = useState('');
    const [planCategories, setPlanCategories] = useState('');
    const [dietCategories, setDietCategories] = useState('');

    const isPlansV2 = planType === 'v2';
    const isImperfectLegacy = planType === 'IF';
    const isDietaryPreferences = dietaryPreferences.dietaryPrefSplit !== null;
    const showCartPreferences = account?.numOrders <= 4;

    const handlePlanSettingsEditClick = () => {
        setIsEditing(true);
        trackEvent('plan settings edit click');
    };

    useEffect(() => {
        if (isDietaryPreferences) {
            const selectedAdditionalPreferences = [];
            if (kosher) selectedAdditionalPreferences.push('Kosher');
            if (glutenFree) selectedAdditionalPreferences.push('Gluten Free');
            if (nonGMO) selectedAdditionalPreferences.push('Non GMO');
            if (percentOrganic > 0)
                selectedAdditionalPreferences.push(
                    `Organic (${ORGANIC_OPTIONS.find((o) => percentOrganic === o.value)?.shortLabel})`
                );
            const dietaryCategories = selectedAdditionalPreferences.join(', ') || '';
            setDietCategories(dietaryCategories);
        }
        if (autoShip) {
            if (isPlansV2 || isImperfectLegacy) {
                const selectedPlanCategories = isImperfectLegacy ? addOnPlans : plans || [];
                const foodPlanCategories = selectedPlanCategories?.map((cat) => cat.name).join(', ') || '';
                setPlanCategories(foodPlanCategories);
                setPlanRange(`$${minBoxRange} - $${maxBoxRange}`);
            } else {
                const planV1Range = plansV1Options?.find((plan) => planID === plan.id)?.planRange || '';
                setPlanRange(planV1Range);
            }
        }
    }, [account]);

    let shippingCopy = `$${shippingFee} shipping fee`;
    if (freeShippingEligible)
        shippingCopy = `$${shippingFee} or FREE SHIPPING on orders above $${freeShippingThreshold}`;

    if (inPlusMembershipTreatment && (membership || trial)) {
        if (plusMembershipShippingFees?.plusMembershipShippingFee === 0) {
            shippingCopy = `All orders ship free with ${plusMembershipBranding}`;
        }
        if (
            plusMembershipShippingFees?.plusMembershipShippingFee &&
            plusMembershipShippingFees?.plusMembershipShippingSavings
        ) {
            shippingCopy = `Save $${plusMembershipShippingFees.plusMembershipShippingSavings} on your shipping fee with ${plusMembershipBranding}`;
        }
    }

    return (
        <Card marginClasses="mt-20 scroll-mt-[100px]" id="plan-summary">
            <div className="grow gap-70 md:flex">
                <h2 className="text-body-lg mb-20 font-grotesk-bold md:min-w-[180px]">Plan Summary</h2>

                <div className="w-full">
                    <p className="text-body-sm text-grey">SHIPPING</p>
                    <p className="text-body-md">{shippingCopy}</p>

                    <p className="text-body-md">${cartMinimum} order minimum to ship</p>

                    <Divider />

                    <div>
                        {autoShip ? (
                            <div>
                                {isEditing ? (
                                    <div id="planSize">
                                        {error !== '' && (
                                            <div className="mb-10">
                                                <Alert variant="error">{error}</Alert>
                                            </div>
                                        )}
                                        <EditPlansModule
                                            account={account}
                                            setIsEditing={setIsEditing}
                                            setError={setError}
                                            isDietaryPreferences={isDietaryPreferences}
                                            isImperfectLegacy={isImperfectLegacy}
                                            isPlansV2={isPlansV2}
                                        />
                                    </div>
                                ) : (
                                    <div className="relative">
                                        <div>
                                            <h4 className="text-body-md font-grotesk-bold">Plan Settings</h4>

                                            {isDietaryPreferences && (
                                                <>
                                                    <p className="text-body-sm mb-5 mt-20 text-grey">SHOPPING FOR</p>
                                                    <p>
                                                        [{householdSize}], [
                                                        {
                                                            COOKING_FREQUENCY.find(
                                                                (freq) => cookingFrequency === freq.value
                                                            )?.shortLabel
                                                        }
                                                        ]
                                                    </p>
                                                    {showCartPreferences && (
                                                        <>
                                                            <p className="text-body-sm mb-5 mt-20 text-grey">
                                                                CART PREFERENCES
                                                            </p>
                                                            <ul className="list-disc pl-20">
                                                                <li className="capitalize">{dietaryPreference}</li>
                                                                {dietCategories?.length > 0 && (
                                                                    <li>{dietCategories}</li>
                                                                )}
                                                                <li>{planCategories}</li>
                                                            </ul>
                                                        </>
                                                    )}
                                                </>
                                            )}
                                            {(isPlansV2 || (isImperfectLegacy && autoShip)) &&
                                                !isDietaryPreferences &&
                                                showCartPreferences && (
                                                    <>
                                                        <p className="text-body-sm mb-5 mt-20 text-grey">
                                                            CART PREFERENCES
                                                        </p>
                                                        {isImperfectLegacy && (
                                                            <p>
                                                                {basePlan?.name}, {basePlan?.size} Pack Size
                                                            </p>
                                                        )}
                                                        <p>{planCategories}</p>
                                                    </>
                                                )}

                                            <p className="text-body-sm mb-5 mt-20 text-grey">PLAN RANGE</p>
                                            <p>{planRange} per week</p>
                                        </div>
                                        <button
                                            className="text-body-sm link absolute right-20 top-0 ml-auto md:right-40"
                                            type="button"
                                            onClick={handlePlanSettingsEditClick}
                                        >
                                            Edit
                                        </button>

                                        <p className="text-body-sm mt-5">
                                            <span className="relative top-[3px] mr-5 inline-block rounded-full bg-[#FAEAC0] p-[1px]">
                                                <RecurringIcon width={16} />
                                            </span>
                                            Any Recurring items will be added to your cart on top of your plan range to
                                            account for your favorite items. Add to your list{' '}
                                            <Link className="link text-body-sm" to="/account/food-preferences">
                                                here
                                            </Link>{' '}
                                            to get first dibs on items, subject to availability.
                                        </p>

                                        <Divider />

                                        <TaxesApply />
                                    </div>
                                )}
                            </div>
                        ) : (
                            <TaxesApply />
                        )}
                    </div>
                </div>
            </div>
        </Card>
    );
}

PlanSummary.propTypes = {
    account: PropTypes.shape({}),
    autoShip: PropTypes.bool.isRequired,
};

PlanSummary.defaultProps = {
    account: {},
};

export default PlanSummary;
